/**
 * Project-level crew assignment (A2, D-3): who's on the PROJECT — no task
 * needed. Backed by GET/PUT /projects/:id/assignments. Mutations invalidate the
 * scheduling surfaces that read assignment blocks (dispatch / today / schedule /
 * calendar) as well as the project itself.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProjectAssignmentsSchema,
  type ProjectAssignments,
  type ProjectAssignmentsSetInput,
} from '@vari-crew/schemas';
import { ApiError, apiCall } from '@/lib/api';

export const projectAssignmentsKey = (projectId: string) =>
  ['project-assignments', projectId] as const;

export function useProjectAssignments(projectId: string | undefined, enabled = true) {
  return useQuery<ProjectAssignments>({
    queryKey: projectAssignmentsKey(projectId ?? ''),
    enabled: enabled && !!projectId,
    queryFn: async () => {
      const raw = await apiCall(`/projects/${projectId}/assignments`);
      return ProjectAssignmentsSchema.parse(raw);
    },
  });
}

/** Set the full crew set on the project (the picker's Save). */
export function useSetProjectAssignments(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectAssignments, ApiError, ProjectAssignmentsSetInput>({
    mutationFn: async (body) => {
      const raw = await apiCall(`/projects/${projectId}/assignments`, { method: 'PUT', body });
      return ProjectAssignmentsSchema.parse(raw);
    },
    onSuccess: (data) => {
      qc.setQueryData(projectAssignmentsKey(projectId), data);
      qc.invalidateQueries({ queryKey: ['projects', 'detail', projectId] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
      qc.invalidateQueries({ queryKey: ['project-calendar'] });
    },
  });
}

/** The API refused because someone already holds an overlapping block elsewhere. */
export function isDoubleBooked(err: unknown): err is ApiError & { details: { crew: string[] } } {
  return err instanceof ApiError && err.code === 'DOUBLE_BOOKED';
}
